import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse, IApiPaginatedResponse, IApiGeneralParams } from "@/types";
import type {
  IDigitalSystem,
  ICreateDigitalSystemPayload,
  IUpdateDigitalSystemPayload,
  IEmployeeDigitalAccess,
  IAssignEmployeeDigitalAccessPayload,
} from "@/types/(company)/onboarding-settings";
import {
  API_COMPANY_DIGITAL_SYSTEMS,
  API_COMPANY_DIGITAL_SYSTEM_BY_ID,
  API_COMPANY_DIGITAL_SYSTEM_TOGGLE,
  API_COMPANY_EMPLOYEE_DIGITAL_ACCESS,
  API_COMPANY_EMPLOYEE_DIGITAL_ACCESS_ALL,
  API_COMPANY_EMPLOYEE_DIGITAL_ACCESS_DELETE,
} from "@/constants/apiEndpoints";

export const companyOnboardingSettingsApi = createApi({
  reducerPath: "companyOnboardingSettingsApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: ["digitalSystems", "digitalSystemDetail", "employeeDigitalAccess"],
  endpoints: (builder) => ({

    getDigitalSystems: builder.query<IApiPaginatedResponse<IDigitalSystem[]>, IApiGeneralParams | void>({
      query: (params) => ({ url: API_COMPANY_DIGITAL_SYSTEMS, params: params ?? {} }),
      providesTags: ["digitalSystems"],
    }),

    getDigitalSystemById: builder.query<IApiResponse<IDigitalSystem>, number>({
      query: (id) => ({ url: API_COMPANY_DIGITAL_SYSTEM_BY_ID(id) }),
      providesTags: (_r, _e, id) => [{ type: "digitalSystemDetail", id }],
    }),

    createDigitalSystem: builder.mutation<IApiResponse<IDigitalSystem>, ICreateDigitalSystemPayload>({
      query: (body) => ({ url: API_COMPANY_DIGITAL_SYSTEMS, method: "POST", body }),
      invalidatesTags: ["digitalSystems"],
    }),

    updateDigitalSystem: builder.mutation<IApiResponse<IDigitalSystem>, { id: number; body: IUpdateDigitalSystemPayload }>({
      query: ({ id, body }) => ({ url: API_COMPANY_DIGITAL_SYSTEM_BY_ID(id), method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => ["digitalSystems", { type: "digitalSystemDetail", id }],
    }),

    toggleDigitalSystem: builder.mutation<IApiResponse<IDigitalSystem>, number>({
      query: (id) => ({ url: API_COMPANY_DIGITAL_SYSTEM_TOGGLE(id), method: "PATCH" }),
      invalidatesTags: ["digitalSystems"],
    }),

    deleteDigitalSystem: builder.mutation<IApiResponse<null>, number>({
      query: (id) => ({ url: API_COMPANY_DIGITAL_SYSTEM_BY_ID(id), method: "DELETE" }),
      invalidatesTags: ["digitalSystems"],
    }),

    // ── Employee Digital Access ──────────────────────────────────────────────

    getEmployeeDigitalAccess: builder.query<IApiResponse<IEmployeeDigitalAccess[]>, number>({
      query: (employeeId) => ({ url: API_COMPANY_EMPLOYEE_DIGITAL_ACCESS(employeeId) }),
      providesTags: (_r, _e, employeeId) => [{ type: "employeeDigitalAccess", id: employeeId }],
    }),

    getEmployeeDigitalAccessAll: builder.query<IApiResponse<IEmployeeDigitalAccess[]>, number>({
      query: (employeeId) => ({ url: API_COMPANY_EMPLOYEE_DIGITAL_ACCESS_ALL(employeeId) }),
      providesTags: (_r, _e, employeeId) => [{ type: "employeeDigitalAccess", id: employeeId }],
    }),

    assignEmployeeDigitalAccess: builder.mutation<
      IApiResponse<IEmployeeDigitalAccess[]>,
      { employeeId: number; body: IAssignEmployeeDigitalAccessPayload }
    >({
      query: ({ employeeId, body }) => ({
        url: API_COMPANY_EMPLOYEE_DIGITAL_ACCESS(employeeId),
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { employeeId }) => [{ type: "employeeDigitalAccess", id: employeeId }],
    }),

    removeEmployeeDigitalAccess: builder.mutation<
      IApiResponse<null>,
      { employeeId: number; id: number }
    >({
      query: ({ employeeId, id }) => ({
        url: API_COMPANY_EMPLOYEE_DIGITAL_ACCESS_DELETE(employeeId, id),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { employeeId }) => [{ type: "employeeDigitalAccess", id: employeeId }],
    }),

  }),
});

export const {
  useGetDigitalSystemsQuery,
  useGetDigitalSystemByIdQuery,
  useCreateDigitalSystemMutation,
  useUpdateDigitalSystemMutation,
  useToggleDigitalSystemMutation,
  useDeleteDigitalSystemMutation,
  useGetEmployeeDigitalAccessQuery,
  useGetEmployeeDigitalAccessAllQuery,
  useAssignEmployeeDigitalAccessMutation,
  useRemoveEmployeeDigitalAccessMutation,
} = companyOnboardingSettingsApi;
